import { useEffect } from 'react';
import { SecurityMonitor } from '@/utils/security';
import { useFeatureFlags } from '@/providers/FeatureFlagProvider';

export const SecurityInitializer = () => {
  const { flags } = useFeatureFlags();

  useEffect(() => {
    const initSecurity = async () => {
      try {
        const monitor = SecurityMonitor.getInstance();

        // CSP (no-breakage default: disabled unless explicitly enabled)
        if (flags.SECURITY_CSP_ENABLED) {
          const cspDirectives = [
            "default-src 'self'",
            // Keep permissive defaults during rollout; tighten later behind flags
            "script-src 'self' 'unsafe-inline'",
            "style-src 'self' 'unsafe-inline'",
            "img-src 'self' data: https:",
            "font-src 'self' data:",
            `connect-src 'self' https://wxgazidevakcnkrngykk.supabase.co`,
            "frame-src 'self'",
            "object-src 'none'",
            "base-uri 'self'"
          ];

          const meta = document.createElement('meta');
          meta.httpEquiv = flags.SECURITY_CSP_ENFORCE
            ? 'Content-Security-Policy'
            : 'Content-Security-Policy'; // Meta report-only is not supported broadly; avoid enforcing by default with flags
          meta.content = cspDirectives.join('; ');
          document.head.appendChild(meta);
        }

        // Additional security headers (safe defaults only)
        if (flags.SECURITY_EXTRA_HEADERS) {
          const headers = [
            { name: 'X-Content-Type-Options', content: 'nosniff' },
            // Avoid breaking iframes by default; enable DENY only via flag
            ...(flags.SECURITY_XFRAME_DENY ? [{ name: 'X-Frame-Options', content: 'DENY' }] : []),
            { name: 'X-XSS-Protection', content: '1; mode=block' },
            { name: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' },
            { name: 'Permissions-Policy', content: 'geolocation=(), camera=(), microphone=()' }
          ];

          headers.forEach(header => {
            const meta = document.createElement('meta');
            meta.httpEquiv = header.name;
            meta.content = header.content;
            document.head.appendChild(meta);
          });
        }

        await monitor.logSecurityEvent('security_initialized', {
          timestamp: new Date().toISOString(),
          csp_enabled: !!flags.SECURITY_CSP_ENABLED,
          csp_enforce: !!flags.SECURITY_CSP_ENFORCE,
          security_headers_set: !!flags.SECURITY_EXTRA_HEADERS,
        });

        console.log('Security system initialized (no-breakage rollout)');
      } catch (error) {
        console.error('Security initialization failed:', error);
      }
    };

    initSecurity();
  }, [flags.SECURITY_CSP_ENABLED, flags.SECURITY_CSP_ENFORCE, flags.SECURITY_EXTRA_HEADERS, flags.SECURITY_XFRAME_DENY]);

  return null;
};